/**
 * Renderer palettes.
 *
 * The canvas cannot read CSS custom properties cheaply per frame, so the board keeps its
 * own palette objects that mirror the design tokens in `src/styles/tokens.css`. Both are
 * derived from the same values; changing one means changing the other.
 */
export interface RenderTheme {
  readonly id: 'light' | 'dark' | 'contrast';
  readonly background: number;
  readonly boardSurface: number;
  readonly gridDot: number;
  readonly gridDotAlpha: number;
  readonly arrow: number;
  readonly arrowBlocked: number;
  readonly arrowPressed: number;
  readonly hint: number;
  readonly focus: number;
  readonly plateAlpha: number;
}

export const LIGHT_THEME: RenderTheme = {
  id: 'light',
  background: 0xf7f5f0,
  boardSurface: 0xfffdf8,
  gridDot: 0x8a8578,
  gridDotAlpha: 0.28,
  arrow: 0x24262b,
  arrowBlocked: 0xc0392f,
  arrowPressed: 0x0f6f5c,
  hint: 0x0f6f5c,
  focus: 0x1f6feb,
  plateAlpha: 0.16,
};

export const DARK_THEME: RenderTheme = {
  id: 'dark',
  background: 0x14161a,
  boardSurface: 0x1c1f25,
  gridDot: 0xb9c0cc,
  gridDotAlpha: 0.2,
  arrow: 0xe9ecf2,
  arrowBlocked: 0xff7a6b,
  arrowPressed: 0x4fd1b0,
  hint: 0x4fd1b0,
  focus: 0x79b8ff,
  plateAlpha: 0.22,
};

export const CONTRAST_THEME: RenderTheme = {
  id: 'contrast',
  background: 0x000000,
  boardSurface: 0x000000,
  gridDot: 0xffffff,
  gridDotAlpha: 0.45,
  arrow: 0xffffff,
  arrowBlocked: 0xffd400,
  arrowPressed: 0x00e5ff,
  hint: 0x00e5ff,
  focus: 0xffd400,
  plateAlpha: 0.4,
};

export function selectRenderTheme(dark: boolean, highContrast: boolean): RenderTheme {
  if (highContrast) return CONTRAST_THEME;
  return dark ? DARK_THEME : LIGHT_THEME;
}
